import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { DirectorCommandName } from '@efira/protocol';

import { useCatalog, useMe, useSetScene } from '../api/queries';

/**
 * Состояние сессии консоли - НАСТОЯЩЕЕ, а не заглушка этапа 1.
 *
 * «Игра запущена» для консоли - это АКТИВНАЯ СЦЕНА аккаунта, а не живое
 * подключение к TikTok: у эталона игра идёт в превью и без эфира, и наш overlay
 * рисует ту сцену, что записана в аккаунте. Поэтому запуск игры - это
 * `setScene(gameId)`, а завершение сессии - `setScene(null)`.
 *
 * Отсюда же экран узнаёт, какие команды ведущего принимает текущая игра: список
 * приезжает каталогом вместе с остальными полями игры, и кнопка управления
 * рисуется по нему, а не по догадке.
 */
export interface ConsoleSession {
  /** Название запущенной игры или null, когда сессии нет. */
  game: string | null;
  /** Идентификатор запущенной игры. */
  gameId: string | null;
  /** Команды ведущего, которые принимает текущая игра. */
  commands: readonly DirectorCommandName[];
  /** Идёт ли сейчас загрузка аккаунта: экран не должен мигать пустотой. */
  loading: boolean;
  start: (gameId: string) => void;
  end: () => void;
}

const SessionContext = createContext<ConsoleSession | null>(null);

export function ConsoleSessionProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const catalog = useCatalog();
  const setScene = useSetScene();

  const gameId = me.data?.user?.activeSceneId ?? null;
  const game = catalog.data?.games.find((item) => item.id === gameId) ?? null;

  const start = useCallback(
    (next: string) => {
      setScene.mutate(next);
    },
    [setScene],
  );

  const end = useCallback(() => {
    setScene.mutate(null);
  }, [setScene]);

  const value = useMemo<ConsoleSession>(
    () => ({
      // Название берём из каталога; пока каталог едет, показываем
      // идентификатор - он всё равно осмысленнее пустоты.
      game: gameId === null ? null : (game?.title['ru'] ?? game?.title['en'] ?? gameId),
      gameId,
      commands: game?.directorCommands ?? [],
      loading: me.data === undefined,
      start,
      end,
    }),
    [game, gameId, me.data, start, end],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useConsoleSession(): ConsoleSession {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useConsoleSession вызван вне ConsoleSessionProvider');
  }
  return value;
}
